import { useState, useEffect, useCallback, useRef } from 'react';
import logger from '../utils/logger';
import { 
  getInterruptedBlankSessions, deleteBlankSession,
  getAllBlankSessionChunks, markBlankSessionRecovering,
} from '../idb/chunk-db';
import { blankRecordingService } from '../services/blankRecordingService';

export interface RecoverableSession {
  recordingId: string;
  createdAt: number;
  duration: number;
  hasScreen: boolean;
  hasWebcam: boolean;
  hasAudio: boolean;
  chunkCount: number;
  estimatedSizeMB: number;
}

export interface UseBlankRecordingRecoveryResult {
  recoverableSessions: RecoverableSession[];
  isDetecting: boolean;
  isRecovering: boolean;
  recoveryProgress: { current: number; total: number; stage: string } | null;
  recoverSession: (recordingId: string) => Promise<boolean>;
  discardSession: (recordingId: string) => Promise<void>;
  discardAll: () => Promise<void>;
  refreshSessions: () => Promise<void>;
}

export function useBlankRecordingRecovery(isUploading?: boolean): UseBlankRecordingRecoveryResult {
  const [recoverableSessions, setRecoverableSessions] = useState<RecoverableSession[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [recoveryProgress, setRecoveryProgress] = useState<{ current: number; total: number; stage: string } | null>(null);
  const hasDetected = useRef(false);
  const prevIsUploading = useRef<boolean | undefined>(undefined);
  const detectRequestId = useRef(0);
  const recoveryCooldownRef = useRef(false);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const detectSessions = useCallback(async () => {
    if (recoveryCooldownRef.current) {
      logger.info('[BlankRecovery] Skipping detection during recovery cooldown');
      return;
    }
    const requestId = ++detectRequestId.current;
    setIsDetecting(true);
    
    try {
      const sessions = await getInterruptedBlankSessions();
      
      if (requestId !== detectRequestId.current) return;
      
      if (sessions.length === 0) {
        setRecoverableSessions([]);
        setIsDetecting(false);
        return;
      }
      
      const recoverable: RecoverableSession[] = [];
      
      for (const session of sessions) {
        const allChunks = await getAllBlankSessionChunks(session.recordingId);
        
        if (allChunks.length === 0) {
          await deleteBlankSession(session.recordingId);
          continue;
        }
        
        const allSegments = [
          ...session.screenSegments,
          ...session.webcamSegments,
          ...session.audioSegments,
        ];
        const maxEnd = Math.max(...allSegments.map((s: any) => s.tsEnd || s.tsStart || 0), 0);
        
        const totalSize = allChunks.reduce((sum, c) => sum + c.blob.size, 0);
        
        const hasScreenChunks = allChunks.some(c => c.streamType === 'screen');
        const hasWebcamChunks = allChunks.some(c => c.streamType === 'webcam');
        const hasAudioChunks = allChunks.some(c => c.streamType === 'audio');
        
        recoverable.push({
          recordingId: session.recordingId,
          createdAt: session.createdAt,
          duration: maxEnd,
          hasScreen: session.screenSegments.length > 0 || hasScreenChunks,
          hasWebcam: session.webcamSegments.length > 0 || hasWebcamChunks,
          hasAudio: session.audioSegments.length > 0 || hasAudioChunks,
          chunkCount: allChunks.length,
          estimatedSizeMB: Math.round(totalSize / (1024 * 1024) * 10) / 10,
        });
      }
      
      if (requestId !== detectRequestId.current) return;
      
      setRecoverableSessions(recoverable);
      
      if (recoverable.length > 0) {
        logger.info('[BlankRecovery] Found recoverable sessions', { count: recoverable.length });
      }
    } catch (error) {
      if (requestId === detectRequestId.current) {
        logger.warn('[BlankRecovery] Detection failed', error);
      }
    } finally {
      if (requestId === detectRequestId.current) {
        setIsDetecting(false);
      }
    }
  }, []);
  
  useEffect(() => {
    if (!hasDetected.current) {
      hasDetected.current = true;
      detectSessions();
    }
  }, [detectSessions]);
  
  useEffect(() => {
    if (prevIsUploading.current === true && isUploading === false) {
      logger.info('[BlankRecovery] Upload completed, re-detecting sessions');
      detectSessions();
    }
    prevIsUploading.current = isUploading;
  }, [isUploading, detectSessions]);
  
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    };
  }, []);
  
  const refreshSessions = useCallback(async () => {
    await detectSessions();
  }, [detectSessions]);
  
  const recoverSession = useCallback(async (recordingId: string): Promise<boolean> => {
    setIsRecovering(true);
    setRecoveryProgress({ current: 0, total: 0, stage: 'Preparing...' });
    
    try {
      const sessions = await getInterruptedBlankSessions();
      const session = sessions.find(s => s.recordingId === recordingId);
      if (!session) {
        logger.warn('[BlankRecovery] Session not found in IDB, removing from list', { recordingId });
        setRecoverableSessions(prev => prev.filter(s => s.recordingId !== recordingId));
        return false;
      }
      
      await markBlankSessionRecovering(recordingId);
      
      const allChunks = await getAllBlankSessionChunks(recordingId);
      if (allChunks.length === 0) {
        logger.warn('[BlankRecovery] No chunks found, cleaning up', { recordingId });
        await deleteBlankSession(recordingId);
        setRecoverableSessions(prev => prev.filter(s => s.recordingId !== recordingId));
        return false;
      }
      
      const groupedChunks: Map<string, typeof allChunks> = new Map();
      for (const chunk of allChunks) {
        const key = `${chunk.streamType}:${chunk.segmentIndex}`;
        if (!groupedChunks.has(key)) {
          groupedChunks.set(key, []);
        }
        groupedChunks.get(key)!.push(chunk);
      }
      
      for (const [, chunks] of groupedChunks) {
        chunks.sort((a, b) => a.chunkIndex - b.chunkIndex);
      }
      
      const totalUploads = groupedChunks.size;
      let completedUploads = 0;
      
      const uploadedScreenSegments: any[] = [];
      const uploadedWebcamSegments: any[] = [];
      const uploadedAudioSegments: any[] = [];
      
      for (const [groupKey, chunks] of groupedChunks) {
        const [streamType, segmentIndexStr] = groupKey.split(':');
        const segmentIndex = parseInt(segmentIndexStr, 10);
        
        setRecoveryProgress({ 
          current: completedUploads, 
          total: totalUploads, 
          stage: `Uploading ${streamType} segment ${segmentIndex + 1}...` 
        });
        
        const mimeType = streamType === 'audio' ? 'audio/webm;codecs=opus' : 'video/webm';
        const segmentBlob = new Blob(chunks.map(c => c.blob), { type: mimeType });
        
        let uploadUrl: string;
        let uploadKey: string;
        
        if (streamType === 'audio') {
          const result = await blankRecordingService.getAudioSegmentUploadUrl(recordingId, segmentIndex);
          uploadUrl = result.url;
          uploadKey = result.key;
        } else {
          const result = await blankRecordingService.getSegmentUploadUrl(
            recordingId, 
            streamType as 'screen' | 'webcam', 
            segmentIndex
          );
          uploadUrl = result.url;
          uploadKey = result.key;
        }
        
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          body: segmentBlob,
          headers: {
            'Content-Type': mimeType,
          },
        });
        
        if (!response.ok) {
          throw new Error(`Upload failed for ${streamType} segment ${segmentIndex}: ${response.status}`);
        }
        
        let segmentMeta: any;
        if (streamType === 'screen') {
          segmentMeta = session.screenSegments.find((s: any) => s.segmentIndex === segmentIndex);
          if (!segmentMeta) {
            const screenLayout = session.layout?.screen;
            segmentMeta = {
              segmentIndex,
              tsStart: 0,
              tsEnd: null,
              hasAudio: false,
              x: screenLayout?.x ?? 0,
              y: screenLayout?.y ?? 0,
              width: screenLayout?.width ?? 1920,
              height: screenLayout?.height ?? 1080,
              isCircle: false,
              cornerRadius: session.screenCornerRadius ?? 0,
            };
            logger.info('[BlankRecovery] Constructed fallback screen segment metadata', { segmentIndex });
          }
          uploadedScreenSegments.push({ ...segmentMeta, key: uploadKey, uploadStatus: 'completed' });
        } else if (streamType === 'webcam') {
          segmentMeta = session.webcamSegments.find((s: any) => s.segmentIndex === segmentIndex);
          if (!segmentMeta) {
            const webcamLayout = session.layout?.webcam;
            segmentMeta = {
              segmentIndex,
              tsStart: 0,
              tsEnd: null,
              hasAudio: false,
              x: webcamLayout?.x ?? 0,
              y: webcamLayout?.y ?? 0,
              width: webcamLayout?.width ?? 320,
              height: webcamLayout?.height ?? 240,
              isCircle: webcamLayout?.isCircle ?? false,
              cornerRadius: webcamLayout?.cornerRadius ?? 0,
              isAvatar: session.avatarWebcam ?? false,
              avatarUrl: session.avatarUrl ?? null,
            };
            logger.info('[BlankRecovery] Constructed fallback webcam segment metadata', { segmentIndex });
          }
          uploadedWebcamSegments.push({ ...segmentMeta, key: uploadKey, uploadStatus: 'completed' });
        } else if (streamType === 'audio') {
          segmentMeta = session.audioSegments.find((s: any) => s.segmentIndex === segmentIndex);
          if (!segmentMeta) {
            segmentMeta = {
              segmentIndex,
              tsStart: 0,
              tsEnd: null,
              hasAudio: true,
            };
            logger.info('[BlankRecovery] Constructed fallback audio segment metadata', { segmentIndex });
          }
          uploadedAudioSegments.push({ ...segmentMeta, key: uploadKey, uploadStatus: 'completed' });
        }
        
        completedUploads++;
      }
      
      setRecoveryProgress({ current: completedUploads, total: totalUploads, stage: 'Finalizing...' });
      
      const allSegments = [
        ...uploadedScreenSegments,
        ...uploadedWebcamSegments,
        ...uploadedAudioSegments,
      ];
      
      const estimatedDuration = (session.lastUpdatedAt && session.createdAt)
        ? session.lastUpdatedAt - session.createdAt
        : 0;
      
      for (const seg of allSegments) {
        if (seg.tsEnd === null || seg.tsEnd === undefined || seg.tsEnd <= seg.tsStart) {
          seg.tsEnd = estimatedDuration > 0 
            ? seg.tsStart + estimatedDuration 
            : seg.tsStart + 30000;
          logger.info('[BlankRecovery] Estimated tsEnd for segment', { 
            tsStart: seg.tsStart, tsEnd: seg.tsEnd, estimatedDuration 
          });
        }
      }
      
      const allSegmentEnds = allSegments.map((s: any) => s.tsEnd ?? 0);
      const duration = allSegmentEnds.length > 0 ? Math.max(...allSegmentEnds) : estimatedDuration;
      
      const completeRequest: any = {
        recordingId,
        streams: {} as any,
        layout: session.layout,
        duration,
        micEnabled: session.micEnabled,
        hasDedicatedAudioStream: session.hasDedicatedAudioStream,
      };
      
      if (uploadedScreenSegments.length > 0) {
        if (uploadedScreenSegments.length > 1) {
          completeRequest.streams.screen = { segments: uploadedScreenSegments };
        } else {
          const seg = uploadedScreenSegments[0];
          completeRequest.streams.screen = {
            key: seg.key,
            tsStart: seg.tsStart ?? 0,
            tsEnd: seg.tsEnd ?? duration,
            hasAudio: seg.hasAudio,
            x: seg.x,
            y: seg.y,
            width: seg.width,
            height: seg.height,
            isCircle: seg.isCircle,
            cornerRadius: seg.cornerRadius,
          };
        }
      }
      
      if (uploadedWebcamSegments.length > 0) {
        if (uploadedWebcamSegments.length > 1) {
          completeRequest.streams.webcam = { segments: uploadedWebcamSegments };
        } else {
          const seg = uploadedWebcamSegments[0];
          completeRequest.streams.webcam = {
            key: seg.key,
            tsStart: seg.tsStart ?? 0,
            tsEnd: seg.tsEnd ?? duration,
            hasAudio: seg.hasAudio,
            x: seg.x,
            y: seg.y,
            width: seg.width,
            height: seg.height,
            isCircle: seg.isCircle,
            cornerRadius: seg.cornerRadius,
            isAvatar: seg.isAvatar,
            avatarUrl: seg.avatarUrl,
          };
        }
      }
      
      if (uploadedAudioSegments.length > 0) {
        completeRequest.streams.audio = { segments: uploadedAudioSegments };
      }
      
      await blankRecordingService.complete(completeRequest);
      
      recoveryCooldownRef.current = true;
      
      await deleteBlankSession(recordingId);
      
      setRecoverableSessions(prev => prev.filter(s => s.recordingId !== recordingId));
      
      logger.info('[BlankRecovery] Session recovered successfully', { recordingId });
      return true;
    } catch (error: any) {
      logger.error('[BlankRecovery] Recovery failed', error);
      setRecoveryProgress(null);
      return false;
    } finally {
      setIsRecovering(false);
      setRecoveryProgress(null);
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = setTimeout(() => {
        recoveryCooldownRef.current = false;
        cooldownTimerRef.current = null;
      }, 2000);
    }
  }, []);
  
  const discardSession = useCallback(async (recordingId: string) => {
    try {
      await deleteBlankSession(recordingId);
      setRecoverableSessions(prev => prev.filter(s => s.recordingId !== recordingId));
      logger.info('[BlankRecovery] Session discarded', { recordingId });
    } catch (error) {
      logger.warn('[BlankRecovery] Failed to discard session', error);
    }
  }, []);
  
  const discardAll = useCallback(async () => {
    for (const session of recoverableSessions) {
      await discardSession(session.recordingId);
    }
  }, [recoverableSessions, discardSession]);
  
  return {
    recoverableSessions,
    isDetecting,
    isRecovering,
    recoveryProgress,
    recoverSession,
    discardSession,
    discardAll,
    refreshSessions,
  };
}
